import { useEffect, useState } from "react"
import { Loader2, ShieldCheck, Lock } from "lucide-react"
import apiClient from "../api"
import LoginPage from "./LoginPage"

export default function AuthWrapper({ children }) {
  const [loading, setLoading] = useState(true)
  const [authenticated, setAuthenticated] = useState(false)
  const [checking, setChecking] = useState(true)
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    async function checkToken() {
      try {
        setChecking(true)
        const result = await apiClient.validate()
        setAuthenticated(!!result)
        if (!!result) {
          setShowSuccess(true)
        }
      } catch (error) {
        console.error("Auth validation failed:", error)
        setAuthenticated(false)
        setShowSuccess(false)
      } finally {
        setLoading(false)
        setTimeout(() => setChecking(false), 300)
      }
    }
    
    const timer = setTimeout(() => {
      checkToken()
    }, 500)
    
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (authenticated && showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false)
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [authenticated, showSuccess])

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 md:p-6">
        <div className="max-w-sm w-full space-y-6 md:space-y-8 text-center">
          {/* Animated gradient background for mobile */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/20 via-black to-black -z-10" />
          
          {/* Animated particles effect */}
          <div className="fixed inset-0 -z-10 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-900/5 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-900/5 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="flex justify-center">
            <div className="relative">
              {/* Outer ring animation */}
              <div className="absolute inset-0 animate-spin-slow">
                <div className="h-24 w-24 md:h-28 md:w-28 rounded-full border-2 border-transparent border-t-indigo-500/30 border-r-purple-500/20"></div>
              </div>
              
              {/* Main lock icon */}
              <div className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-gradient-to-br from-gray-900 to-black border border-gray-800 flex items-center justify-center relative overflow-hidden">
                {/* Inner glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-600/10 blur-sm" />
                <Lock className="h-10 w-10 md:h-12 md:w-12 text-indigo-400 relative z-10" />
              </div>
              
              {/* Pulsing dots around the lock */}
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className={`absolute h-2 w-2 md:h-3 md:w-3 bg-indigo-500 rounded-full ${
                    i % 2 === 0 ? 'animate-ping' : 'animate-pulse'
                  }`}
                  style={{
                    top: `${Math.sin((i * Math.PI) / 2) * 40}px`,
                    left: `${Math.cos((i * Math.PI) / 2) * 40}px`,
                    animationDelay: `${i * 0.2}s`
                  }}
                />
              ))}
            </div>
          </div>
          
          <div className="space-y-3 md:space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              {checking ? "Verifying Session" : "Loading"}
            </h2>
            <p className="text-gray-400 text-sm md:text-base px-4">
              {checking 
                ? "Checking your authentication status..."
                : "Getting everything ready..."
              }
            </p>
          </div>
          
          {/* Animated progress bar */}
          <div className="pt-4 md:pt-6 px-6 md:px-8">
            <div className="relative">
              <div className="h-1.5 md:h-2 bg-gray-900 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-full animate-[shimmer_2s_ease-in-out_infinite]"
                  style={{
                    backgroundSize: '200% 100%',
                    backgroundPosition: '100% 0'
                  }}
                />
              </div>
              {/* Glowing effect under progress bar */}
              <div className="absolute -bottom-1 left-0 right-0 h-1 bg-indigo-500/20 blur-sm" />
            </div>
          </div>
          
          {/* Status indicator */}
          <div className="pt-6 md:pt-8">
            <div className="inline-flex items-center gap-2 text-xs md:text-sm text-gray-500 bg-gray-900/50 backdrop-blur-sm px-4 py-2 md:py-3 rounded-full border border-gray-800">
              <div className="h-2 w-2 md:h-2.5 md:w-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full animate-pulse" />
              <span className="text-gray-400">Secure connection established</span>
            </div>
          </div>
          
          {/* Loading dots for smaller screens */}
          <div className="pt-4 md:hidden">
            <div className="flex justify-center space-x-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-2 w-2 bg-indigo-400 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!authenticated) {
    return <LoginPage onLogin={() => {
      setAuthenticated(true)
      setShowSuccess(true)
    }} />
  }

  return (
    <>
      {showSuccess && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center p-4 animate-fadeOut">
          {/* Background particles */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute h-1 w-1 bg-emerald-500/30 rounded-full animate-pulse"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${i * 0.1}s`
                }}
              />
            ))}
          </div>
          
          <div className="text-center space-y-4 md:space-y-6 relative">
            {/* Success icon with glow */}
            <div className="relative">
              {/* Outer rings */}
              <div className="absolute inset-0 animate-ping-slow">
                <div className="h-28 w-28 md:h-32 md:w-32 rounded-full border-2 border-emerald-500/30 mx-auto"></div>
              </div>
              <div className="absolute inset-0 animate-ping-slow delay-300">
                <div className="h-36 w-36 md:h-40 md:w-40 rounded-full border-2 border-emerald-500/20 mx-auto"></div>
              </div>
              
              {/* Main icon */}
              <div className="h-24 w-24 md:h-28 md:w-28 rounded-full bg-gradient-to-br from-emerald-900/30 to-black border border-emerald-800/50 flex items-center justify-center mx-auto relative overflow-hidden">
                {/* Inner glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-green-500/10 blur-lg" />
                <ShieldCheck className="h-12 w-12 md:h-14 md:w-14 text-emerald-400 relative z-10" />
              </div>
            </div>
            
            <div className="space-y-2 md:space-y-3">
              <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent animate-slideUp"
                  style={{ animationDelay: '0ms' }}>
                Authentication Successful!
              </h3>
              <p className="text-gray-400 text-sm md:text-base animate-slideUp"
                 style={{ animationDelay: '100ms' }}>
                Redirecting to your dashboard...
              </p>
            </div>
            
            {/* Progress indicator */}
            <div className="pt-4 md:pt-6 animate-slideUp"
                 style={{ animationDelay: '200ms' }}>
              <div className="inline-flex flex-col md:flex-row items-center gap-3 text-xs md:text-sm text-gray-500">
                <div className="flex space-x-1.5">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="h-2 w-2 md:h-2.5 md:w-2.5 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    ></div>
                  ))}
                </div>
                <span className="text-gray-400">Loading your content</span>
              </div>
              
              {/* Mobile progress bar */}
              <div className="mt-4 md:hidden w-48 mx-auto">
                <div className="h-1 bg-gray-900 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full animate-[shimmer_1.5s_ease-in-out_infinite]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main content with fade-in animation */}
      <div className={`${showSuccess ? 'opacity-0' : 'opacity-100 animate-fadeIn'} transition-opacity duration-500 bg-black`}>
        {children}
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        
        .animate-fadeOut {
          animation: fadeOut 0.3s ease-out forwards;
          animation-delay: 1.2s;
        }
        
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; visibility: hidden; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-slideUp {
          animation: slideUp 0.5s ease-out;
        }
        
        @keyframes slideUp {
          from { transform: translateY(10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  )
}